import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { networkManager, TOKEN_KEY, REFRESH_TOKEN_KEY } from '../api/NetworkManager';
import { getCurrentUser, emulateUser } from '../api';
import type { CurrentUser } from '../api';
import { queryClient } from '../lib/query-client';

// While emulating, the admin's own tokens are parked here so "Exit" restores the real session.
const RETURN_TOKEN_KEY = 'byte_billing_return_access';
const RETURN_REFRESH_KEY = 'byte_billing_return_refresh';

interface AuthState {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True when the current session is an admin viewing-as another user (read-only). */
  isEmulating: boolean;
  logout: () => void;
  /** Persist the OTP-issued tokens and prime the API client (call before navigating). */
  setTokens: (accessToken: string, refreshToken: string) => void;
  /** Admin: start a read-only emulation of a YUM_ID. Parks the admin session, swaps in emulation tokens. */
  emulate: (yumId: string) => Promise<void>;
  /** Restore the parked admin session and leave emulation. */
  exitEmulation: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setTokens = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    networkManager.setToken(accessToken);
  }, []);

  // Tear down all client-side session state — tokens, the auth header, the in-memory user, and the
  // React Query cache (so one user's data never leaks into the next). Does NOT navigate.
  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    networkManager.setToken(null);
    setUser(null);
    queryClient.clear();
  }, []);

  /** Swap the active session to a new token pair and re-resolve the user, without touching the parked
   * admin tokens. Clears the query cache so no data bleeds between identities. */
  const swapTo = useCallback(async (accessToken: string, refreshToken?: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    networkManager.setToken(accessToken);
    queryClient.clear();
    setUser(await getCurrentUser());
  }, []);

  const emulate = useCallback(async (yumId: string) => {
    // Park the admin's own tokens first — Exit restores them.
    sessionStorage.setItem(RETURN_TOKEN_KEY, localStorage.getItem(TOKEN_KEY) ?? '');
    sessionStorage.setItem(RETURN_REFRESH_KEY, localStorage.getItem(REFRESH_TOKEN_KEY) ?? '');
    const res = await emulateUser(yumId);
    await swapTo(res.accessToken, res.refreshToken);
    window.location.assign('/'); // land where the emulated user lands (role-driven)
  }, [swapTo]);

  const exitEmulation = useCallback(async () => {
    const access = sessionStorage.getItem(RETURN_TOKEN_KEY);
    const refresh = sessionStorage.getItem(RETURN_REFRESH_KEY);
    sessionStorage.removeItem(RETURN_TOKEN_KEY);
    sessionStorage.removeItem(RETURN_REFRESH_KEY);
    if (!access) { clearSession(); window.location.href = '/'; return; }
    await swapTo(access, refresh ?? undefined);
    window.location.assign('/admin/users');
  }, [swapTo, clearSession]);

  const logout = useCallback(() => {
    sessionStorage.removeItem(RETURN_TOKEN_KEY);
    sessionStorage.removeItem(RETURN_REFRESH_KEY);
    clearSession();
    window.location.href = '/';
  }, [clearSession]);

  // Restore the session on mount from the stored access token.
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    networkManager.setToken(storedToken);
    getCurrentUser()
      .then(setUser)
      .catch((err: unknown) => {
        // Only clear the session on a genuine auth failure (401 after refresh). A transient error —
        // the API restarting locally, a network blip, a 5xx — must NOT wipe a still-valid session,
        // otherwise restarting the dev server logs you out even though the token is good. (On Lambda
        // the API is always up, so this never triggered there.)
        const status = (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status;
        if (status === 401) clearSession();
      })
      .finally(() => setIsLoading(false));
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, isLoading, isEmulating: !!user?.emulatedBy, logout, setTokens, emulate, exitEmulation }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
